import React, { useState, useEffect } from 'react';
import { Camera, CheckCircle, XCircle, Save, Loader, Eye, EyeOff } from 'lucide-react';
import { peopleCountingService } from '../lib/supabaseClient';
import { useTheme } from '../App';

const PeopleCountingInstellingen = ({ showNotification }) => {
  const { t } = useTheme();
  const [settings, setSettings] = useState({
    camera_ip: '',
    camera_username: '',
    camera_password: '',
    camera_channel: '1',
    polling_interval: '60',
    retention_days: '90',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const data = await peopleCountingService.getSettings();

        // Convert array of settings to object
        const settingsObj = {};
        data.forEach(setting => {
          settingsObj[setting.setting_key] = setting.setting_value;
        });

        setSettings(prevSettings => ({
          ...prevSettings,
          ...settingsObj
        }));
      } catch (error) {
        console.error('Error loading settings:', error);
        showNotification('Fout bij laden van instellingen', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [showNotification]);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setTestResult(null); // Clear test result when settings change
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);

      const result = await peopleCountingService.testConnection();

      setTestResult(result);

      if (result.success) {
        showNotification('Verbinding succesvol! ' + (result.message || ''), 'success');
      } else {
        showNotification('Verbinding mislukt: ' + (result.error || 'Onbekende fout'), 'error');
      }
    } catch (error) {
      console.error('Test connection error:', error);
      setTestResult({
        success: false,
        error: error.message
      });
      showNotification('Fout bij testen verbinding: ' + error.message, 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Save each setting
      await peopleCountingService.updateSetting('camera_ip', settings.camera_ip, false);
      await peopleCountingService.updateSetting('camera_username', settings.camera_username, true);
      await peopleCountingService.updateSetting('camera_password', settings.camera_password, true);
      await peopleCountingService.updateSetting('camera_channel', settings.camera_channel, false);
      await peopleCountingService.updateSetting('polling_interval', settings.polling_interval, false);
      await peopleCountingService.updateSetting('retention_days', settings.retention_days, false);

      showNotification('Instellingen succesvol opgeslagen', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showNotification('Fout bij opslaan: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-zf-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-bold ${t.text}`}>Personen Telling Instellingen</h1>
        <p className={`${t.textSecondary} mt-1`}>Configureer de Dahua camera verbinding</p>
      </div>

      {/* Camera Configuration */}
      <div className={`${t.bg} ${t.border} border rounded-lg p-6`}>
        <div className="flex items-center gap-3 mb-6">
          <Camera className={`w-6 h-6 ${t.text}`} />
          <h2 className={`text-xl font-semibold ${t.text}`}>Camera Configuratie</h2>
        </div>

        <div className="space-y-4">
          {/* IP Address */}
          <div>
            <label className={`block ${t.text} text-sm font-medium mb-2`}>
              IP Adres
            </label>
            <input
              type="text"
              value={settings.camera_ip}
              onChange={(e) => handleChange('camera_ip', e.target.value)}
              placeholder="192.168.1.108"
              className={`w-full px-4 py-2 ${t.input} ${t.border} border rounded-lg focus:outline-none focus:ring-2 focus:ring-zf-accent`}
            />
          </div>

          {/* Username */}
          <div>
            <label className={`block ${t.text} text-sm font-medium mb-2`}>
              Gebruikersnaam
            </label>
            <input
              type="text"
              value={settings.camera_username}
              onChange={(e) => handleChange('camera_username', e.target.value)}
              placeholder="admin"
              className={`w-full px-4 py-2 ${t.input} ${t.border} border rounded-lg focus:outline-none focus:ring-2 focus:ring-zf-accent`}
            />
          </div>

          {/* Password */}
          <div>
            <label className={`block ${t.text} text-sm font-medium mb-2`}>
              Wachtwoord
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={settings.camera_password}
                onChange={(e) => handleChange('camera_password', e.target.value)}
                placeholder="••••••••"
                className={`w-full px-4 py-2 pr-10 ${t.input} ${t.border} border rounded-lg focus:outline-none focus:ring-2 focus:ring-zf-accent`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-2 top-1/2 -translate-y-1/2 ${t.textSecondary} hover:${t.text}`}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Channel */}
          <div>
            <label className={`block ${t.text} text-sm font-medium mb-2`}>
              Channel
            </label>
            <input
              type="number"
              value={settings.camera_channel}
              onChange={(e) => handleChange('camera_channel', e.target.value)}
              min="1"
              className={`w-full px-4 py-2 ${t.input} ${t.border} border rounded-lg focus:outline-none focus:ring-2 focus:ring-zf-accent`}
            />
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-900/20 border border-green-500' : 'bg-red-900/20 border border-red-500'}`}>
              <div className="flex items-center gap-2">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <span className={testResult.success ? 'text-green-400' : 'text-red-400'}>
                  {testResult.success
                    ? testResult.message || 'Verbinding succesvol'
                    : testResult.error || 'Verbinding mislukt'}
                </span>
              </div>
              {testResult.data && (
                <div className={`mt-2 text-sm ${t.textSecondary}`}>
                  <p>Device: {testResult.data.deviceType}</p>
                  {testResult.data.serialNumber && <p>Serial: {testResult.data.serialNumber}</p>}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleTestConnection}
              disabled={testing || !settings.camera_ip || !settings.camera_username || !settings.camera_password}
              className={`${t.buttonSecondary} px-6 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {testing ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Test Verbinding
            </button>

            <button
              onClick={handleSave}
              disabled={saving}
              className={`${t.button} px-6 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50`}
            >
              {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Opslaan
            </button>
          </div>
        </div>
      </div>

      {/* System Settings */}
      <div className={`${t.bg} ${t.border} border rounded-lg p-6`}>
        <h2 className={`text-xl font-semibold ${t.text} mb-6`}>Systeem Instellingen</h2>

        <div className="space-y-4">
          {/* Polling Interval */}
          <div>
            <label className={`block ${t.text} text-sm font-medium mb-2`}>
              Synchronisatie Interval (seconden)
            </label>
            <input
              type="number"
              value={settings.polling_interval}
              onChange={(e) => handleChange('polling_interval', e.target.value)}
              min="10"
              max="3600"
              className={`w-full px-4 py-2 ${t.input} ${t.border} border rounded-lg focus:outline-none focus:ring-2 focus:ring-zf-accent`}
            />
            <p className={`${t.textSecondary} text-xs mt-1`}>
              Hoe vaak data van de camera wordt opgehaald (standaard: 60 seconden)
            </p>
          </div>

          {/* Retention Days */}
          <div>
            <label className={`block ${t.text} text-sm font-medium mb-2`}>
              Data Bewaarperiode (dagen)
            </label>
            <input
              type="number"
              value={settings.retention_days}
              onChange={(e) => handleChange('retention_days', e.target.value)}
              min="1"
              max="365"
              className={`w-full px-4 py-2 ${t.input} ${t.border} border rounded-lg focus:outline-none focus:ring-2 focus:ring-zf-accent`}
            />
            <p className={`${t.textSecondary} text-xs mt-1`}>
              Oude data wordt automatisch verwijderd na deze periode (standaard: 90 dagen)
            </p>
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className={`${t.button} px-6 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50`}
            >
              {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Opslaan
            </button>
          </div>
        </div>
      </div>

      {/* Info Panel */}
      <div className={`${t.bg} ${t.border} border rounded-lg p-4`}>
        <h3 className={`${t.text} font-semibold mb-2`}>Let op:</h3>
        <ul className={`${t.textSecondary} text-sm space-y-1 list-disc list-inside`}>
          <li>Gebruikersnaam en wachtwoord worden versleuteld opgeslagen</li>
          <li>Test de verbinding voordat je de instellingen opslaat</li>
          <li>De camera moet bereikbaar zijn op het netwerk (poort 80)</li>
          <li>Wijzigingen in het synchronisatie interval worden pas actief na herstart</li>
        </ul>
      </div>
    </div>
  );
};

export default PeopleCountingInstellingen;
